import React, { FC, ReactNode } from 'react';

import {
  BuildingIcon,
  BuildingIconVariant,
  ChevronLeftIcon,
  EventBuyBack,
  EventClassConversion,
  EventFundraising,
  EventGrant,
  EventIssueShares,
  EventSecondaries,
  EventValuation,
  UploadIcon,
} from '../../../../assets/icons/index';
import { AppFormattedMessage } from '../../../../components/app-formatted-message/index';
import Button from '../../../../components/button/index';
import { EventFormType } from '../../../../types/events.types';
import { useCompanyIncorporationContext } from '../../hooks/use-company-incorporation-context.hook';
import { ChildrenFormProps } from '../event-form.types';

type EventTypeButtonProps = {
  children: ReactNode;
  description: ReactNode | string;
  handleNext: () => void;
  title: ReactNode | string;
};

const EventTypeButton: FC<EventTypeButtonProps> = ({
  title,
  description,
  children,
  handleNext,
}) => {
  return (
    <Button
      className="flex items-center justify-between gap-4 py-2"
      onClick={handleNext}
      styleType="NONE"
    >
      <div className="flex items-center gap-4">
        <div className="rounded-lg border-[1px] border-[#F2F2F2] bg-white p-[9px]">{children}</div>
        <div className="flex flex-col items-start gap-1">
          <span className="text-left text-sm font-[550] text-[#172335]">{title}</span>
          <span className="text-left text-label-sm font-normal text-gray-500">{description}</span>
        </div>
      </div>
      <ChevronLeftIcon className="size-4 rotate-180 transform" />
    </Button>
  );
};

export type InitialStepProps = ChildrenFormProps;

export const InitialStep: FC<InitialStepProps> = ({
  handleCloseModal,
  nextFormStep,
  setFormData,
  companyId,
}) => {
  const { data: incorporationContext, isLoading: isIncorporationContextLoading } =
    useCompanyIncorporationContext(companyId);

  const hasExistingEvents = incorporationContext?.hasExistingEvents ?? false;
  const hasCompanyIncorporation = incorporationContext?.hasCompanyIncorporation ?? false;
  const showCompanyIncorporationOption = !hasCompanyIncorporation;

  const handleNext = (value: EventFormType) => {
    setFormData('initialStep.type', value);
    nextFormStep();
  };

  const companyIncorporationButton = (highlighted: boolean) => (
    <EventTypeButton
      description={<AppFormattedMessage id="COMPANY_INCORPORATION_DESCRIPTION" />}
      handleNext={() => handleNext(EventFormType.COMPANY_INCORPORATION)}
      title={<AppFormattedMessage id="COMPANY_INCORPORATION" />}
    >
      <BuildingIcon
        variant={highlighted ? BuildingIconVariant.FOUNDATION : BuildingIconVariant.EVENT}
      />
    </EventTypeButton>
  );

  return (
    <>
      <div className="invisible-y-scroll flex h-full flex-col gap-4 rounded-b-xl border-b border-gray-300 bg-white p-6 pt-0">
        {showCompanyIncorporationOption && !hasExistingEvents && !isIncorporationContextLoading && (
          <div className="relative mt-2 rounded-xl border border-brand-50 bg-brand-25 p-4">
            <span className="absolute right-6 top-0 rounded-bl-[4px] rounded-br-[4px] rounded-tl-[4px] rounded-tr-none bg-brand-50 px-2 py-0.5 text-[10px] font-[500] text-brand-700">
              <AppFormattedMessage id="FOUNDATION" />
            </span>
            {companyIncorporationButton(true)}
          </div>
        )}
        <div className="flex flex-col gap-1 rounded-xl bg-gray-50 p-4">
          {showCompanyIncorporationOption && hasExistingEvents && (
            <>
              {companyIncorporationButton(false)}
              <div className="h-[1px] w-full bg-gray-100" />
            </>
          )}
          <EventTypeButton
            description={<AppFormattedMessage id="EMPOWER_STAKEHOLDERS" />}
            handleNext={() => handleNext(EventFormType.GRANT)}
            title={<AppFormattedMessage id="GRANT" />}
          >
            <EventGrant />
          </EventTypeButton>
          <EventTypeButton
            description={<AppFormattedMessage id="BULK_IMPORT_GRANTS" />}
            handleNext={() => handleNext(EventFormType.BULK_IMPORT_GRANT)}
            title={<AppFormattedMessage id="BULK_IMPORT_GRANT" />}
          >
            <UploadIcon className="size-5 rotate-180 transform" iconColor="#344054" />
          </EventTypeButton>

          <div className="h-[1px] w-full bg-gray-100" />
          <EventTypeButton
            description={<AppFormattedMessage id="INITIATE_FUNDRAISING" />}
            handleNext={() => handleNext(EventFormType.FUNDRAISING_ROUND)}
            title={<AppFormattedMessage id="FUNDRAISING_ROUND" />}
          >
            <EventFundraising />
          </EventTypeButton>

          <div className="h-[1px] w-full bg-gray-100" />
          <EventTypeButton
            description={<AppFormattedMessage id="DISTRIBUTE_SHARES" />}
            handleNext={() => handleNext(EventFormType.SHARE_ISSUANCE)}
            title={<AppFormattedMessage id="ISSUE_SHARES" />}
          >
            <EventIssueShares />
          </EventTypeButton>

          <div className="h-[1px] w-full bg-gray-100" />
          <EventTypeButton
            description={<AppFormattedMessage id="TRANSFER_SHARES" />}
            handleNext={() => handleNext(EventFormType.SECONDARIES)}
            title={<AppFormattedMessage id="SECONDARIES" />}
          >
            <EventSecondaries />
          </EventTypeButton>

          <div className="h-[1px] w-full bg-gray-100" />
          <EventTypeButton
            description={<AppFormattedMessage id="SHARE_BUYBACKS" />}
            handleNext={() => handleNext(EventFormType.BUYBACK)}
            title={<AppFormattedMessage id="BUYBACK_DECREASE" />}
          >
            <EventBuyBack />
          </EventTypeButton>
          <EventTypeButton
            description={<AppFormattedMessage id="CONVERT_SHARE_CLASSES" />}
            handleNext={() => handleNext(EventFormType.CONVERSION)}
            title={<AppFormattedMessage id="CLASS_CONVERSION" />}
          >
            <EventClassConversion />
          </EventTypeButton>
          <EventTypeButton
            description={<AppFormattedMessage id="COMPANY_VALUATION" />}
            handleNext={() => handleNext(EventFormType.VALUATION)}
            title={<AppFormattedMessage id="VALUATION" />}
          >
            <EventValuation />
          </EventTypeButton>
        </div>
      </div>

      <div className="flex h-9 w-full justify-between gap-3 px-6">
        <Button
          className="w-fit bg-transparent px-4 py-[6px] text-sm font-[450] text-gray-700"
          onClick={handleCloseModal}
          styleType="NONE"
          type="button"
        >
          <AppFormattedMessage id="CANCEL" />
        </Button>
      </div>
    </>
  );
};
